// "New ticket" Dialog. Reuses the agent form's required-field UX:
//   • <ErrorSummary> at the top, populated on submit fail with click-to-
//     focus links into the offending fields.
//   • aria-required on the input drives the destructive-border state when
//     the inline error appears.
//   • Editing a field with an error clears it live so the summary shrinks.
//
// Plan-gated priority Select: the support route returns
// plan_limit_exceeded for high/urgent on free/starter — we mirror the
// gate in the UI by disabling those options with a hint label so the
// user doesn't hit the server error. The server still enforces.

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy } from 'lucide-react';
import {
  SUPPORT_PRIORITIES,
  type SupportPriority,
  type SupportTicketDetail,
} from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { friendlyError } from '../../lib/error-message.js';
import { supportApi, SUPPORT_QUERY_KEY } from '../../lib/api/support.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { Button } from '../../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import {
  ErrorSummary,
  type ErrorSummaryItem,
} from '../../components/ui/error-summary.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { Textarea } from '../../components/ui/textarea.js';
import { toast } from '../../components/ui/toaster.js';

type FieldKey = 'subject' | 'description' | 'priority';
const FIELD_LABELS: Record<FieldKey, string> = {
  subject: 'Subject',
  description: 'Description',
  priority: 'Priority',
};

// Pro-only priorities. Keep in sync with SUPPORT_PRIORITY_BY_PLAN in
// @cortex/shared/support.ts; both are checked but this is the friendly
// pre-flight UX.
const PRO_ONLY: ReadonlySet<SupportPriority> = new Set(['high', 'urgent']);

interface NewTicketDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (detail: SupportTicketDetail) => void;
}

export function NewTicketDialog({ open, onClose, onCreated }: NewTicketDialogProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  // Read the current plan code from the same query the rest of the
  // dashboard already uses. Likely cached when the user lands on /support.
  const usage = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });
  const planCode = usage.data?.plan.code ?? 'free';
  const isPro = planCode === 'pro';

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<SupportPriority>('normal');
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [submitFailCount, setSubmitFailCount] = useState(0);

  const create = useMutation({
    mutationFn: () =>
      supportApi.create(api, {
        subject: subject.trim(),
        description,
        priority,
      }),
    onSuccess: (detail) => {
      toast.success(`Ticket #${detail.ticket.id.slice(0, 8)} created`, {
        description: "We'll reply on this page.",
      });
      void queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEY });
      onCreated?.(detail);
      reset();
      onClose();
    },
    onError: (err) => {
      // Plan-gate failures from the server land here. friendlyError
      // surfaces the route's error message verbatim
      // ("priority \"high\" is not available on plan starter; ...").
      toast.error('Could not create ticket', {
        description: friendlyError(err, 'Failed to file the ticket.'),
      });
    },
  });

  function reset(): void {
    setSubject('');
    setDescription('');
    setPriority('normal');
    setErrors({});
    setSubmitFailCount(0);
  }

  function update(key: FieldKey, value: string): void {
    if (key === 'subject') setSubject(value);
    else if (key === 'description') setDescription(value);
    else setPriority(value as SupportPriority);

    // Auto-clear the field's error so the summary shrinks live.
    // Destructure-omit instead of `delete next[key]` to satisfy
    // no-dynamic-delete (idiomatic immutable equivalent).
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _omitted, ...rest } = prev;
      return rest;
    });
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const next: Partial<Record<FieldKey, string>> = {};
    if (subject.trim().length === 0) next.subject = 'required';
    if (description.trim().length === 0) next.description = 'required';
    // Client-side mirror of the server gate; the server is still the
    // authority and would 429 if the user forges a high-priority POST.
    if (!isPro && PRO_ONLY.has(priority)) {
      next.priority = 'requires the Pro plan — upgrade to use this priority';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      setSubmitFailCount((n) => n + 1);
      return;
    }
    setErrors({});
    create.mutate();
  }

  const errorList: ErrorSummaryItem[] = (
    Object.entries(errors) as [FieldKey, string | undefined][]
  ).flatMap(([key, message]) =>
    message
      ? [{ fieldId: `support-field-${key}`, label: FIELD_LABELS[key], message }]
      : [],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" aria-hidden />
            New support ticket
          </DialogTitle>
          <DialogDescription>
            Tell us what's wrong. We'll reply on this page — no email thread to
            chase.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <ErrorSummary errors={errorList} focusKey={submitFailCount} />

          <div className="space-y-1.5">
            <Label
              htmlFor="support-field-subject"
              className='after:ml-0.5 after:text-destructive after:content-["*"]'
            >
              Subject
            </Label>
            <Input
              id="support-field-subject"
              value={subject}
              onChange={(e) => { update('subject', e.target.value); }}
              maxLength={200}
              placeholder="One-line summary"
              aria-required
              aria-invalid={errors.subject ? true : undefined}
              autoFocus
            />
            {errors.subject && (
              <p className="text-xs text-destructive">{errors.subject}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="support-field-description"
              className='after:ml-0.5 after:text-destructive after:content-["*"]'
            >
              Description
            </Label>
            <Textarea
              id="support-field-description"
              value={description}
              onChange={(e) => { update('description', e.target.value); }}
              maxLength={10_000}
              rows={6}
              placeholder="Steps you tried, error messages, what you expected to happen…"
              aria-required
              aria-invalid={errors.description ? true : undefined}
            />
            {errors.description && (
              <p className="text-xs text-destructive">{errors.description}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-field-priority">Priority</Label>
            <Select
              id="support-field-priority"
              value={priority}
              onChange={(e) => { update('priority', e.target.value); }}
              aria-invalid={errors.priority ? true : undefined}
            >
              {SUPPORT_PRIORITIES.map((p) => {
                const proGated = !isPro && PRO_ONLY.has(p);
                return (
                  <option key={p} value={p} disabled={proGated}>
                    {p[0]?.toUpperCase()}
                    {p.slice(1)}
                    {proGated ? ' — Pro plan only' : ''}
                  </option>
                );
              })}
            </Select>
            {errors.priority && (
              <p className="text-xs text-destructive">{errors.priority}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => { onClose(); reset(); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Filing…' : 'File ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
